import { useCallback, useEffect, useState } from 'react';

interface PushState {
  supported: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
  endpoint?: string;
}

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

export function usePushNotifications() {
  const [state, setState] = useState<PushState>({ supported: false, permission: 'default', subscribed: false });

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    const permission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
    setState((s) => ({ ...s, supported, permission }));
    if (!supported) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setState((s) => ({ ...s, subscribed: Boolean(sub), endpoint: sub?.endpoint }));
    }).catch(() => {});
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return 'denied' as NotificationPermission;
    const p = await Notification.requestPermission();
    setState((s) => ({ ...s, permission: p }));
    return p;
  }, []);

  const subscribe = useCallback(async () => {
    if (!state.supported) return false;
    const permission = await requestPermission();
    if (permission !== 'granted') return false;
    const registration = await navigator.serviceWorker.ready;
    // Public VAPID key should be configured in env at build time; fallback to demo string
    const vapidPublicKey = (window as any).__VAPID_PUBLIC_KEY__ || import.meta.env?.VITE_VAPID_PUBLIC_KEY || '';
    const options: PushSubscriptionOptionsInit = { userVisibleOnly: true } as any;
    if (vapidPublicKey) (options as any).applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    const subscription = await registration.pushManager.subscribe(options);
    const res = await fetch('/api/admin/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subscription),
    });
    if (!res.ok) throw new Error('Failed to save subscription');
    setState((s) => ({ ...s, subscribed: true, endpoint: subscription.endpoint }));
    return true;
  }, [state.supported, requestPermission]);

  return { ...state, requestPermission, subscribe };
}


